/* eslint-disable zillow/@typescript-eslint/naming-convention */
import type { ShopperPlatformContextConfig } from '@zillow/shopper-platform-config';

import type { getOmpDataQuery } from '../__generated__/graphql';
import { cookie } from '../constants/cookies';
import { NEW_YORK_ZIPS } from '../constants/newYorkZipCodes';
import env from '../env.factory';
import { createApolloClient } from '../graphql/apolloUtils';
import { fetchHomepageGdpData } from '../graphql/fetchHomepageGdpData';
import { ompQuery } from '../graphql/query';
import { getHomepageSpecificAbs, isTreatmentForTrial } from '../utils/abs';
import { isZipInRegion } from '../utils/isZipInRegion';
import { getCentroidFromSearchCookie } from '../utils/searchCookie';
import getSharedSessionState from '../utils/sharedSessionState';
import type { GlobalStoreState, Platforms } from './GlobalStoreProvider';

export enum AllHopsPlacements {
    HOME_PAGE_HERO_IMAGE = 'HomePageHeroImage',
    HOME_PAGE_RTB_CARD_1 = 'HomePageRTBCard1',
    HOME_PAGE_RTB_CARD_2 = 'HomePageRTBCard2',
    HOME_PAGE_RTB_CARD_3 = 'HomePageRTBCard3',
    HOME_PAGE_BANNER = 'HomePageBanner',
}

export const hopsBelowFoldPlacements = [
    AllHopsPlacements.HOME_PAGE_RTB_CARD_1,
    AllHopsPlacements.HOME_PAGE_RTB_CARD_2,
    AllHopsPlacements.HOME_PAGE_RTB_CARD_3,
    AllHopsPlacements.HOME_PAGE_BANNER,
];

interface ApplicationStateProps {
    query?: { ipAddr: string; forceNYFairHousingMsg?: string };
    headers: { [x: string]: string };
    cookies: { [x: string]: string };
}

/**
 * Assembles state
 * @param {ApplicationStateProps} req express request object
 * @returns {GlobalStoreState} initialState for app-level store
 */
export const getInitState = async (
    req: ApplicationStateProps,
    shopperPlatformConfig: ShopperPlatformContextConfig
): Promise<GlobalStoreState> => {
    const { abServiceConfig, zgGraphHost, graphqlTimeout, graphqlHost, s3sConfig, autocomplete } =
        env;
    const apolloClient = createApolloClient({
        requestHeaders: req.headers,
        uri: graphqlHost,
        timeout: graphqlTimeout,
    });
    const ipAddr = req.headers['X-Client-IP'] ?? req.query?.ipAddr ?? '192.211.15.239';
    const platform: Platforms = shopperPlatformConfig.isMobile ? 'mbl' : 'web';
    const guid = req.headers['x-user-guid'] ?? '';

    const variables = {
        isCarouselAdsRestricted: false,
        placements: Object.values(AllHopsPlacements),
    };

    const homepageAbsPromise = getHomepageSpecificAbs(
        guid,
        abServiceConfig,
        shopperPlatformConfig.isMobile
    );
    const sharedSessionStatePromise = getSharedSessionState(guid, s3sConfig, { ipAddr });
    const gdpResponsePromise = fetchHomepageGdpData<getOmpDataQuery>({
        homePagesGraphqlQuery: ompQuery,
        apolloClient,
        variables,
    });

    const homepageAbs = await homepageAbsPromise;
    const sharedSessionState = await sharedSessionStatePromise;
    const gdpResponse = await gdpResponsePromise;

    const { zipcode, city, centroid } = sharedSessionState;

    const centroidFromSearchCookie = getCentroidFromSearchCookie(req.cookies[cookie.search] ?? '');

    const isNLSEnabled = isTreatmentForTrial({
        abs: homepageAbs,
        trial: 'SXP_NATURAL_LANGUAGE_SEARCH',
        treatment: 'ON',
    });

    const forceNYFairHousingMsg = req.query?.forceNYFairHousingMsg === 'true';
    // Show NY Fair Housing Message when in New York State
    const showNYFairHousingMsg = forceNYFairHousingMsg || isZipInRegion(zipcode, NEW_YORK_ZIPS);

    return {
        onsiteMessages: gdpResponse?.data.onsiteMessage ?? {},
        zgGraphHost,
        graphqlTimeout,
        zipcode,
        city,
        abs: homepageAbs,
        centroid: centroidFromSearchCookie ?? centroid,
        platform,
        guid,
        isNLSEnabled,
        showNYFairHousingMsg,
        autocompleteUrl: autocomplete.regionAddressAutocompleteURL,
    };
};
